import { getLeftNavMaxWidth, getListViewMaxWidth } from '../utils/getMaxWidths';
import { isReadingPanePositionBottom, isReadingPanePositionRight } from './readingPanePosition';
import { mergeStyles } from '@fluentui/merge-styles';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { MIN_READING_PANE_WIDTH } from '../internalConstants';
import { type BrowserWidthBucket, getBrowserWidthBucket } from 'owa-layout';
import { getLeftNavWidth } from '../utils/getLeftNavWidth';
import { getListViewDimensions } from '../utils/getListViewDimensions';
import { shouldShowFolderPane } from './shouldShowFolderPane';

/**
 * Gets the styles to be applied to the Left Pane container
 */
export function getLeftPaneStyles(): string {
    // This transition is important: There is a transition on right-pane
    // and this transition is here to match that to ensure both sides animate in sync,
    // otherwise there is a jittering experience where one pane moves instantly and the other lags.
    // We only do this when the folder pane is hidden because when visible, we want
    // any fp resizing to feel instant.
    const transition = shouldShowFolderPane() ? undefined : 'width 0.2s, max-width 0.2s';

    return mergeStyles({
        flex: '0 0 auto',
        width: getLeftNavWidth(),
        maxWidth: getLeftNavMaxWidth(),
        transition,
    });
}

/**
 * Gets the styles to be applied to the list view container
 */
export function getListViewContainerStyles(): string {
    const { listViewWidth, listViewHeight } = getListViewDimensions();
    const isRPositionRight = isReadingPanePositionRight();

    return mergeStyles({
        width: isRPositionRight ? listViewWidth : '',
        maxWidth: isRPositionRight ? getListViewMaxWidth() : '',
        height: isReadingPanePositionBottom() ? listViewHeight : '',
        flex: '0 0 auto',
    });
}

/**
 * Gets the styles to be applied to the wrapper around the card above list view and the list view
 */
export function getListViewPlusCardContainerStyles(hasItemSelected: boolean): string {
    const { listViewWidth, listViewHeight } = getListViewDimensions();
    const isRPositionRight = isReadingPanePositionRight();
    const isRPPositionBottom = isReadingPanePositionBottom();
    const isRPDenselessEnabled = isGlobalFeatureEnabled('mon-type-ramp');

    // When denseless flight is on and nothing selected in bottom layout, we stretch to full height
    const useFullHeightBottom = isRPDenselessEnabled && !hasItemSelected;

    // Spacing offset (1px in denseless mode to preserve ML right border, else 4px legacy)
    const extraSpacing = isRPDenselessEnabled ? 1 : 4;

    // Only apply list view specific width constraints when RP is on the right
    const width = isRPositionRight ? listViewWidth + extraSpacing : '100%';
    const maxWidth = isRPositionRight ? getListViewMaxWidth() + extraSpacing : '100%';

    // We only constrain height when:
    //  - RP is at bottom
    //  - We are NOT in the special full-height denseless state
    const constrainBottom = !useFullHeightBottom && isRPPositionBottom;

    return mergeStyles({
        width,
        maxWidth,
        height: constrainBottom ? listViewHeight : '100%',
        maxHeight: constrainBottom ? '75%' : undefined,
        flex: '0 0 auto',
    });
}

/**
 * Gets the styles to be applied for the reading pane container
 */
export function getReadingPaneContainerStyles(): string {
    let minWidthRP;

    /**
     * Set min width for RP when dynamic layout flight is turned on but not when
     * browser has hit the minimum breakpoint after which we do not want min-width for RP.
     */
    if (getBrowserWidthBucket() != 418) {
        minWidthRP = MIN_READING_PANE_WIDTH;
    }

    return mergeStyles({
        minWidth: minWidthRP,
    });
}
